import type { ReactNode } from "react";

type Stat = { value: string; label: string };
type Flag = { emoji: string; label: string };

type Props = {
    authorNote: string;
    heading: string;
    intro: string;
    stats: Stat[];
    flags: Flag[];
    /** "Before you go" callout body — can include real <Link>s. */
    beforeYouGo: ReactNode;
};

// Full-bleed dark editorial block for a ride report's "trip at a
// glance" — five stat cards, a country-flow chip row, and a
// before-you-go callout. Shares the .gt-guide-opinion* chrome (byline,
// route line, heading, intro) with GuideOpinionLead/GuideStackLead.
export default function GuideTripLead({
    authorNote,
    heading,
    intro,
    stats,
    flags,
    beforeYouGo,
}: Props) {
    return (
        <section className="gt-guide-opinion">
            <svg
                className="gt-guide-opinion__route"
                viewBox="0 0 900 60"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <path d="M-10 40 Q 220 8 460 26 T 920 16" />
            </svg>
            <div className="gt-guide-opinion__inner">
                <div className="gt-guide-opinion__byline">
                    <span className="gt-guide-opinion__avatar" aria-hidden="true">
                        S
                    </span>
                    <div>
                        <div className="gt-eyebrow gt-guide-opinion__eyebrow">
                            The trip
                        </div>
                        <div className="gt-guide-opinion__note">{authorNote}</div>
                    </div>
                </div>
                <h2 className="gt-guide-opinion__heading">{heading}</h2>
                <p className="gt-guide-opinion__intro">{intro}</p>
                <div className="gt-guide-stack gt-guide-stack--stats">
                    {stats.map((s) => (
                        <div key={s.label} className="gt-guide-stack__col">
                            <div className="gt-guide-trip-stats__value">{s.value}</div>
                            <div className="gt-eyebrow gt-guide-stack__label">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
                <div
                    className="gt-guide-opinion__chips gt-guide-trip-flow"
                    aria-label="Countries crossed, in order"
                >
                    {flags.map((f, i) => (
                        <span key={`${f.label}-${i}`} style={{ display: "contents" }}>
                            <span className="gt-guide-opinion__chip">
                                <span aria-hidden="true">{f.emoji}</span> {f.label}
                            </span>
                            {i < flags.length - 1 && (
                                <span
                                    className="gt-guide-trip-flow__arrow"
                                    aria-hidden="true"
                                >
                                    →
                                </span>
                            )}
                        </span>
                    ))}
                </div>
                <div className="gt-guide-backstop">
                    <span aria-hidden="true">🧭</span>
                    <p>{beforeYouGo}</p>
                </div>
            </div>
        </section>
    );
}
